import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import {
  SharedStatesAsyncGetterAPI,
  getSharedStateAsyncGettersAPI,
} from '../config/asyncGetters/createSharedStateAsyncGetters';
import { AvatarFrameId } from '../profile/frames';
import { getBuruFriends } from '../friends/friends.getters';
import { assertNever } from '../utils';
import { getTeamChestEventTargetScore } from './chest/teamChest.getters';
import { canParticipateOnTeamCompetition } from './competition/teamCompetition.getters';
import {
  TeamCompetitor,
  TeamEventData,
  UnlcaimedCompetitionData,
} from './competition/teamCompetition.ruleset';
import {
  getCurrentTeamEventId,
  getTeamEventStartTime,
  getPreviousTeamEventId,
  getTeamEventScore,
  getTeamWeeklyHelps,
  getTeamEventStatus,
  getTeamEventRewards,
  getCurrentTeamEventType,
} from './team.getters';
import { TeamComputedProperties } from './team.properties';
import {
  MAX_MEMBERS,
  MAX_TEAM_SUGGESTIONS,
  DISPLAYED_CHAT_MESSAGES,
  TeamMemberData,
  TeamMemberProfile,
  TeamRole,
  ChatMessageType,
  TeamEventType,
  MAX_TEAM_LEADERBOARD,
} from './team.ruleset';
import { ChatMessage, TeamExtendedProfile, TeamState } from './team.schema';

export type ExtendedChatMessage = ChatMessage & {
  senderName: string;
  canRespond?: boolean;
  seen: boolean;
  contents: { type: ChatMessageType };
};

export const teamAsyncGetters = createAsyncGetters({
  getRecommendedTeams: async (_: void, rawApi) => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    const friendsIds = getBuruFriends(state);

    const weights: number[] = [];
    const results: TeamExtendedProfile[] = [];

    // Search teams with friends.
    const now = api.date.now();
    const friendsTeams = await api.sharedStates.team.search({
      where: {
        members: {
          containsAnyOf: friendsIds,
        },
        memebersCount: {
          greaterThan: 0,
          lessThan: MAX_MEMBERS,
        },
      },
      limit: MAX_TEAM_SUGGESTIONS,
    });

    friendsTeams.results.forEach((team) => {
      const membersIds = team.members;
      const friendsCount = membersIds.filter((member) =>
        friendsIds.includes(member),
      ).length;
      const adminIds = team.admins;
      const adminFriendsCount = adminIds.filter((admin) =>
        friendsIds.includes(admin),
      ).length;

      // Groups with more friends are more likely to be recommended.
      const friendsWeight = Math.min(friendsCount * 0.2, 1);

      // Groups with more members are more likely to be recommended.
      const membersWeight = Math.min(membersIds.length * 0.01, 0.5);

      // Active groups are more likely to be recommended.
      const activityWeight =
        team.currentEventScore || team.previousEventScore ? 1 : 0;

      // Groups with admins that are friends are more likely to be recommended. (Ie. groups created by friends)
      const adminWeight = adminFriendsCount ? 1 : 0;

      // Sum weights.
      weights.push(
        friendsWeight + membersWeight + activityWeight + adminWeight,
      );
      results.push(
        getTeamExtendedProfile(state.team.currentTeamId, team.id, team, now),
      );
    });

    // Sort results by weight descending.
    results.sort((a, b) => {
      const aIndex = results.indexOf(a);
      const bIndex = results.indexOf(b);
      return weights[bIndex] - weights[aIndex];
    });

    // Search random teams.
    const remaining = MAX_TEAM_SUGGESTIONS - results.length;
    const randomTeams = await api.sharedStates.team.search({
      where: {
        memebersCount: {
          greaterThan: 0,
          lessThan: MAX_MEMBERS,
        },
        id: {
          isNotOneOf: results.map((team) => team.id),
        },
      },
      limit: remaining,
      sort: [
        { field: 'currentEventScore', order: 'desc' },
        { field: 'previousEventScore', order: 'desc' },
      ],
    });
    randomTeams.results.forEach((team) => {
      results.push(
        getTeamExtendedProfile(state.team.currentTeamId, team.id, team, now),
      );
    });

    // Remove teams that the user is already a member of.
    return results;
  },

  searchTeams: async ({ query }: { query: string }, rawApi) => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const teams = await api.sharedStates.team.search({
      where: {
        teamName: {
          matchesAnyOf: [query],
        },
        memebersCount: {
          greaterThan: 0,
        },
      },
      limit: MAX_TEAM_SUGGESTIONS,
    });

    const state = await api.getOwnState();

    return teams.results.map((team) =>
      getTeamExtendedProfile(
        state.team.currentTeamId,
        team.id,
        team,
        api.date.now(),
      ),
    );
  },

  getTeamMembers: async (
    { teamId }: { teamId?: string },
    rawApi,
  ): Promise<TeamMemberData[]> => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    teamId = teamId ?? state.team.currentTeamId;
    if (!teamId) return [];

    const team = await api.sharedStates.team.fetch(teamId);

    const membersIds = Object.keys(team?.global.members ?? {});

    const now = api.date.now();
    const currentEventId = getCurrentTeamEventId(now) ?? '';
    const previousEventId = getPreviousTeamEventId(team!, now) ?? '';
    membersIds.sort((a, b) => {
      // Admins at the top.
      if (team?.global.members[a].role === 'admin') return -1;

      // Sort by score on current descending.
      const currentEventScore =
        team?.global.members[a].contributions[currentEventId]?.score ?? 0;
      const currentEventScoreB =
        team?.global.members[b].contributions[currentEventId]?.score ?? 0;
      if (currentEventScoreB !== currentEventScore) {
        return currentEventScoreB - currentEventScore;
      }

      // Sort by score on previous descending.
      const previousEventScore =
        team?.global.members[a].contributions[previousEventId]?.score ?? 0;
      const previousEventScoreB =
        team?.global.members[b].contributions[previousEventId]?.score ?? 0;
      return previousEventScoreB - previousEventScore;
    });

    const profiles = await api.searchPlayers({
      where: {
        id: {
          isOneOf: membersIds,
        },
      },
      limit: membersIds.length,
    });

    const members = membersIds.map((id) => {
      const profile = profiles.results.find((p) => p.id === id);
      const teamProfile = team?.global.members[id];

      return {
        id,
        me: id === api.getUserID(),
        helps: teamProfile?.helps.length ?? 0,
        role: (team?.global.members[id].role ?? 'member') as TeamRole,
        level: profile?.match2Level ?? 1,
        name: profile?.publicProfileName ?? 'Player',
        avatar: profile?.publicProfilePhoto ?? '',
        frame: (profile?.profileAvatarFrame ?? 'default') as AvatarFrameId,
      };
    });

    members.sort((a, b) => b.level - a.level);

    return members.map((member, i) => ({
      id: member.id,
      profile: {
        name: member.name,
        role: member.role,
        avatar: member.avatar,
        frame: member.frame,
      },
      me: member.me,
      helps: member.helps,
      level: member.level,
      position: i + 1,
    }));
  },

  getTeamProfile: async (
    { teamId }: { teamId?: string },
    rawApi,
  ): Promise<TeamExtendedProfile | undefined> => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    // If no teamId is provided, use the current team.
    const state = await api.getOwnState();
    teamId = teamId ?? state.team.currentTeamId;

    // User not in a team. Bail.
    if (!teamId) return undefined;

    // No team state. Bail.
    const team = await api.sharedStates.team.fetch(teamId);
    if (!team) return undefined;

    // Build extended profile.
    return getTeamExtendedProfile(
      state.team.currentTeamId,
      teamId,
      team,
      api.date.now(),
    );
  },

  getCurrentTeamEvent: async (
    _: void,
    rawApi,
  ): Promise<TeamEventData | undefined> => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    const teamId = state.team.currentTeamId;
    if (!teamId) return undefined;

    const teamState = await api.sharedStates.team.fetch(teamId);

    if (!teamState) throw new Error('Team not found');

    const now = api.date.now();
    const currentEventId = getCurrentTeamEventId(now);

    return getEventLeaderboard(teamId, teamState, currentEventId ?? '', api);
  },

  getUnclaimedTeamEvents: async (
    _: void,
    rawApi,
  ): Promise<UnlcaimedCompetitionData[] | undefined> => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    const teamId = state.team.currentTeamId;
    if (!teamId) return undefined;

    const teamState = await api.sharedStates.team.fetch(teamId);

    if (!teamState) throw new Error('Team not found');

    const playerId = state.id;
    const { contributions } = teamState.global.members[playerId];
    const now = api.date.now();
    const unclaimedRewards = Object.keys(contributions).filter((eventId) => {
      // Skip if event does not exist on state global events state
      if (!teamState.global.events[eventId]) return false;

      // Skip if event is chest and score is not enough
      const event = teamState.global.events[eventId];
      if (event.type === 'chest') {
        const targetScore = event.targetScore ?? Infinity;
        if (event.score < targetScore) return false;
      }

      return (
        !contributions[eventId].claimedRewardsAt &&
        getTeamEventStatus({
          team: teamState,
          userId: playerId,
          eventId,
          now,
        }) === 'complete-rewards-available'
      );
    });

    const leaderboards = await Promise.all(
      unclaimedRewards.map(async (eventId) => ({
        eventId,
        eventType: teamState.global.events[eventId].type as TeamEventType,
        leaderboard: await getEventLeaderboard(teamId, teamState, eventId, api),
      })),
    );

    return leaderboards.map(({ eventId, eventType, leaderboard }) => {
      const previousEventId =
        getPreviousTeamEventId(
          teamState,
          getTeamEventStartTime(eventId).getTime(),
        ) ?? '';

      const alreadyFinished = Object.values(teamState.global.members).some(
        (x) => !!x.contributions[eventId]?.claimedRewardsAt,
      );

      return {
        ...leaderboard!,

        rewards: getTeamEventRewards(
          teamState,
          teamId,
          eventId,
          eventType,
          leaderboard!,
        ),
        previousScore:
          teamState.global.members[playerId].contributions[previousEventId]
            ?.score ?? 0,
        previousTeamScore: getTeamEventScore(teamState, previousEventId),
        alreadyFinished,
      };
    });
  },

  getTest: async (_: void, rawApi) => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    const teamId = state.team.currentTeamId;

    return await api.sharedStates.team.fetch(teamId!);
  },

  getTeamMessages: async (args: { lastSeenAt?: number }, rawApi) => {
    const api = getSharedStateAsyncGettersAPI(rawApi);

    const state = await api.getOwnState();
    const teamId = state.team.currentTeamId;

    if (!teamId) return [];

    const team = await api.sharedStates.team.fetch(teamId);

    if (!team) return [];

    const messages = team.global.chat ?? [];
    const blockedUsers =
      state.userSharedStates.team[teamId]?.blockedUsers ?? [];
    const filteredMessages = messages
      .filter((message) => !blockedUsers.includes(message.senderId))
      .slice(-DISPLAYED_CHAT_MESSAGES);

    const profilesToFetch = filteredMessages.map((x) => x.senderId);
    const profiles = await api.searchPlayers({
      where: {
        id: {
          isOneOf: profilesToFetch,
        },
      },
      limit: profilesToFetch.length,
    });
    const profilesMap = profiles.results.reduce(
      (acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      },
      {} as Record<string, (typeof profiles.results)[0]>,
    );

    const lastSeenMessageTimestamp =
      args.lastSeenAt ?? state.team.lastSeenMessageTimestamp;

    const memberData = team.global.members[state.id];

    return filteredMessages.map((message) => {
      // Leaving and rejoining the team allows multiple energy helps,
      // treat having joined after the request as having sent energy.
      const joinedBefore = memberData.joinedAt < message.sentAt;

      // Member data my not exist if the user just joined the team.
      const alreadySent = !!memberData?.helps.find(
        (help) =>
          help.type === 'energyRequest' &&
          help.requestId === message.sentAt &&
          help.target === message.senderId,
      );

      return {
        ...message,
        senderName:
          profilesMap[message.senderId]?.publicProfileName ?? 'Player',
        canRespond:
          message.contents.type === 'energyRequest'
            ? memberData && joinedBefore && !alreadySent
            : undefined,
        seen: message.sentAt <= lastSeenMessageTimestamp,
      };
    }) as ExtendedChatMessage[];
  },

  getTeamsLeaderboardEntries: async (
    _: void,
    rawApi,
  ): Promise<TeamExtendedProfile[]> => {
    const api = getSharedStateAsyncGettersAPI(rawApi);
    const state = await api.getOwnState();
    const teamId = state.team.currentTeamId;
    const teamState = teamId
      ? await api.sharedStates.team.fetch(teamId)
      : undefined;
    return getTeamsLeaderboardEntries({ api, teamId, teamState });
  },
});

async function getEventLeaderboard(
  teamId: string,
  teamState: TeamState,
  eventId: string,
  api: SharedStatesAsyncGetterAPI,
): Promise<TeamEventData | undefined> {
  const now = api.date.now();
  const sharedStateId = teamState?.global.events[eventId]?.sharedStateId;
  if (!sharedStateId) {
    // Matchmaking not done yet. Return all values except the leaderboard.
    const currentEventId = getCurrentTeamEventId(now);
    if (currentEventId === eventId) {
      return {
        id: eventId,
        myTeam: [],
        teams: [],
        sharedStateId,
        canJoin: canParticipateOnTeamCompetition(teamState),
        joined: false,
        status: getTeamEventStatus({
          team: teamState,
          userId: api.getUserID(),
          eventId,
          now,
        }),
        ...getTypeSpecificCompetitionData(
          teamState,
          eventId,
          getCurrentTeamEventType(now)!,
          now,
        ),
      };
    }

    // Previous event that the team has not participated in. Bail.
    return undefined;
  }

  const eventState =
    await api.sharedStates.teamCompetition.fetch(sharedStateId);

  if (!eventState) return undefined;

  const ids = Object.entries(eventState.global.teams).map(([id]) => ({
    id,
  }));

  // Search all team profiles.
  const profiles = await api.sharedStates.team.search({
    where: {
      id: {
        isOneOf: ids.map((team) => team.id),
      },
    },
    limit: ids.length,
  });
  const map = profiles.results.reduce(
    (acc, { id, profile, events }) => {
      acc[id] = {
        profile,
        score: events.find((x) => x.eventId === eventId)?.score ?? 0,
      };
      return acc;
    },
    {} as Record<
      string,
      { profile: (typeof profiles.results)[0]['profile']; score: number }
    >,
  );

  // Make sure current team's profile is included and up to date.
  map[teamId] = {
    profile: teamState.global.profile,
    score: getTeamEventScore(teamState, eventId),
  };

  const leaderboard = ids.map((team) => ({
    ...team,
    profile: map[team.id]?.profile ?? {
      name: 'Team',
      icon: '',
      description: '',
      levelRequired: 0,
    },
    score: map[team.id]?.score ?? 0,
    current: team.id === teamId,
  }));
  leaderboard.sort((a, b) => b.score - a.score);
  const teams = leaderboard.map((team, i) => ({
    ...team,
    position: i + 1,
  }));

  const membersScore = Object.keys(teamState.global.members).map((id) => {
    const member = teamState.global.members[id];
    return {
      id,
      score: member.contributions[eventId]?.score ?? 0,
      helps: member.helps.length,
      role: member.role,
    };
  });

  // Sort by score descending.
  membersScore.sort((a, b) => b.score - a.score);

  // Search all player profiles.
  const playerProfiles = await api.searchPlayers({
    where: {
      id: {
        isOneOf: membersScore.map((member) => member.id),
      },
    },
    limit: membersScore.length,
  });

  const myTeam = membersScore.map((member, i) => {
    const profile = playerProfiles.results.find((p) => p.id === member.id);
    return {
      id: member.id,
      position: i + 1,
      profile: {
        name: profile?.publicProfileName ?? 'Player',
        role: member.role as TeamRole,
        avatar: profile?.publicProfilePhoto ?? '',
        frame: (profile?.profileAvatarFrame ?? 'default') as AvatarFrameId,
      },
      current: member.id === api.getUserID(),
      score: member.score,
    } satisfies TeamCompetitor<TeamMemberProfile>;
  });

  return {
    id: eventId,
    myTeam,
    teams,
    sharedStateId,
    canJoin: canParticipateOnTeamCompetition(teamState),
    joined: !!teamState.global.members[api.getUserID()],
    status: getTeamEventStatus({
      team: teamState,
      userId: api.getUserID(),
      eventId,
      now,
    }),
    ...getTypeSpecificCompetitionData(
      teamState,
      eventId,
      teamState.global.events[eventId].type as TeamEventType,
      now,
    ),
  };
}

async function getTeamsLeaderboardEntries(args: {
  api: SharedStatesAsyncGetterAPI;
  teamId?: string;
  teamState?: TeamState;
}): Promise<TeamExtendedProfile[]> {
  const { api, teamId, teamState } = args;
  const now = api.date.now();
  const eventId = getCurrentTeamEventId(now);

  if (!eventId) return [];

  // Search all team profiles.
  const profiles = await api.sharedStates.team.search({
    where: {
      currentEventScore: {
        greaterThan: 0,
      },
    },
    sort: [
      {
        field: 'currentEventScore',
        order: 'desc',
      },
    ],
    limit: MAX_TEAM_LEADERBOARD,
  });

  return profiles.results.map((team, i) => {
    const extendedProfile = getTeamExtendedProfile(teamId, team.id, team, now);

    return {
      ...extendedProfile,
      position: i + 1,
      score:
        team.id === teamId && teamState
          ? // Fetch the most up to date score for the user's team.
            getTeamEventScore(teamState, eventId)
          : team.currentEventScore,
    };
  });
}

function getTeamExtendedProfile(
  userTeamId: string | undefined,
  teamId: string,
  team: TeamState | TeamComputedProperties,
  now: number,
): TeamExtendedProfile {
  if ('global' in team) {
    const currentEventId = getCurrentTeamEventId(now) ?? '';
    const members = Object.keys(team.global.members);
    return {
      ...team.global.profile,
      id: teamId,
      memberCount: members.length,
      member: userTeamId === teamId,
      score: getTeamEventScore(team, currentEventId),
      helps: getTeamWeeklyHelps(team, now),
    };
  }

  const currentEventId = getCurrentTeamEventId(now) ?? '';
  const members = team.members;
  const event = team.events?.find((x) => x.eventId === currentEventId);
  return {
    ...team.profile,
    id: teamId,
    memberCount: members.length,
    member: userTeamId === teamId,
    score: event?.score ?? 0,
    helps: team.weeklyHelps,
  };
}

function getTypeSpecificCompetitionData(
  team: TeamState,
  eventId: string,
  type: TeamEventType,
  now: number,
) {
  switch (type) {
    case 'competition':
      return {
        type,
        score: getTeamEventScore(team, eventId),
      };
    case 'chest':
      return {
        type,
        score: getTeamEventScore(team, eventId),
        targetScore: getTeamChestEventTargetScore(team, eventId, now),
      };
    default:
      assertNever(type);
  }
}
